import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { client, urlFor } from "../sanityClient";
import { PortableText } from "@portabletext/react";

const ProductDetail = () => {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [wishlistPopup, setWishlistPopup] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);

    useEffect(() => {
        client
            .fetch(
                `*[_type == "product" && slug.current == $slug][0]{
                _id, name, mainImage, gallery, description, excerpt,
                categories[]->{_id, title}
            }`,
                { slug }
            )
            .then((data) => {
                if (!data) return; // Tránh lỗi nếu không có dữ liệu

                setProduct(data);
                setSelectedImage(data.mainImage);

                // Kiểm tra nếu có categories thì mới fetch sản phẩm liên quan
                if (data.categories?.length) {
                    client
                        .fetch(
                            `*[_type == "product" && _id != $id && count(categories[@._ref in $categoryIds]) > 0] | order(_createdAt desc) [0...4] {
                            _id, name, slug, mainImage, excerpt
                        }`,
                            { id: data._id, categoryIds: data.categories.map((cat) => cat._id) }
                        )
                        .then((related) => setRelatedProducts(related))
                        .catch(console.error);
                } else {
                    setRelatedProducts([]); // Nếu không có categories, đặt mảng trống
                }
            })
            .catch(console.error);
    }, [slug]);


    // Xử lý thêm vào Wishlist
    const addToWishlist = (product) => {
        let wishlist = JSON.parse(localStorage.getItem("wishlist")) || [];

        if (wishlist.some((item) => item._id === product._id)) {
            setWishlistPopup({ product, message: "Sản phẩm đã có trong danh sách yêu thích." });
            return;
        }

        wishlist.push(product);
        localStorage.setItem("wishlist", JSON.stringify(wishlist));

        setWishlistPopup({ product, message: "Đã thêm vào danh sách yêu thích." });
    };

    const closePopup = () => {
        setWishlistPopup(null);
    };



    if (!product) return <div className="flex justify-center items-center h-screen text-gray-600">Loading...</div>;

    return (
        <main className="container mx-auto px-4 py-12 font-Inter  pt-[72px] lg:pt-[109px]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 container mx-auto py-12 lg:py-24">
                {/* Hình ảnh sản phẩm */}

                <div className="mx-auto w-full max-w-lg sm:max-w-2xl lg:max-w-4xl ">
                    {/* Ảnh chính */}
                    <div className="relative w-full pt-7 px-3 border-t border-x border-gray-300 bg-gray-200 rounded-t-xl">
                        <img
                            src={urlFor(selectedImage).url()}
                            alt={product.name}
                            className="w-[95%] m-auto h-[350px] sm:h-[400px] md:h-[500px] lg:h-[400px] xl:h-[500px] 2xl:h-[580px] object-cover rounded-t-xl transition-all duration-300"
                        />
                    </div>

                    {/* Gallery hình ảnh */}
                    {product.gallery && product.gallery.length > 0 && (
                        <div className="flex flex-wrap md:grid md:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-2 md:gap-3 mt-4 rounded-b-lg border-x border-b border-gray-300 bg-gray-200 py-2 px-2 overflow-x-auto md:overflow-hidden">
                            {/* Hiển thị ảnh chính đầu tiên trong gallery để có thể chọn lại */}
                            {[product.mainImage, ...product.gallery].map((image, index) => (
                                <img
                                    key={index}
                                    src={urlFor(image).url()}
                                    alt={`Gallery ${index + 1}`}
                                    className={`w-16 h-16 sm:w-20 sm:h-20 object-cover cursor-pointer transition-all duration-300 rounded-xl
                        ${selectedImage === image ? "border-2 border-gray-500 scale-[1.01]" : "border-2 border-white"}
                        hover:scale-[1.01]`}
                                    onClick={() => setSelectedImage(image)}
                                />
                            ))}
                        </div>
                    )}
                </div>


                {/* Thông tin sản phẩm */}
                <div className="space-y-4">
                    <h1 className="text-4xl font-semibold text-indigo-800">{product.name}</h1>
                    <p className="text-lg font-OpenSans font-medium text-gray-700">{product.excerpt}</p>

                    {/* Thể loại sản phẩm */}
                    <div>
                        <span className="font-semibold font-OpenSans text-gray-800">Thể loại: </span>
                        {product.categories.map((category, index) => (
                            <Link
                                key={category._id}
                                to={`/products?category=${category._id}`}
                                className="text-indigo-800 font-OpenSans font-medium hover:underline border-r border-indigo-800 pr-2 mr-2 last:border-none"
                            >
                                {category.title}
                            </Link>
                        ))}
                    </div>

                    {/* Mô tả sản phẩm */}
                    <div className="text-md font-OpenSans text-gray-700 mt-6">
                        <PortableText value={product.description}/>
                    </div>

                    {/* Giá sản phẩm */}
                    <div
                        className="mt-6 p-4 bg-gradient-to-r from-[#33367b] to-[#D4AF37] text-white rounded-xl shadow-md flex items-center justify-between">
                        <span className="text-xl font-OpenSans font-medium uppercase">Giá:</span>
                        <span className="text-xl font-OpenSans font-medium uppercase animate-pulse">Liên hệ</span>
                    </div>

                    {/* Đặc quyền khi mua hàng */}
                    <div className="mt-6 bg-gray-100 p-4 border border-gray-300 font-OpenSans">
                        <h3 className="text-lg font-semibold font text-gray-800 mb-2">Đặc quyền khi hợp tác cùng
                            Timo</h3>
                        <ul className="list-disc list-inside text-gray-700 leading-relaxed">
                            <li>Tiếp cận nguồn vải chất lượng cao
                            </li>
                            <li>Nhận tư vấn chuyên nghiệp
                            </li>
                            <li>Hợp tác sản xuất linh hoạt
                            </li>
                            <li> Timo luôn cập nhật những xu hướng vải mới nhất, giúp
                                bạn đón đầu thị trường.
                            </li>
                        </ul>
                    </div>

                    {/* Ưu đãi hiện tại */}
                    <div className="mt-4 bg-gray-50 p-4 border border-indigo-300 font-OpenSans">
                        <h3 className="text-lg font-semibold text-indigo-800 mb-2">Ưu đãi hợp tác cùng Timo</h3>
                        <ul className="list-disc list-inside text-gray-800 leading-relaxed">
                            <li>Ưu đãi đặc biệt cho khách hàng thân thiết
                            </li>
                            <li>Hỗ trợ thiết kế mẫu
                            </li>
                            <li>Nhận ưu đãi khi giới thiệu đối tác mới cho Timo.
                            </li>

                        </ul>
                    </div>


                    {/* Nút thêm vào wishlist */}
                    <button
                        onClick={() => addToWishlist(product)}
                        className="mt-6 bg-[#33367b] text-white font-medium px-4 py-3 rounded-xl hover:bg-indigo-800 transition"
                    >
                        Liên hệ tư vấn ngay
                    </button>
                </div>

            </div>

            {/* Sản phẩm liên quan */}
            {relatedProducts.length > 0 && (
                <section className="mt-16">
                    <h2 className="text-3xl font-medium text-gray-800 mb-6">Sản phẩm liên quan</h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {relatedProducts.map((product) => (
                            <div className="block shadow-md overflow-hidden transition-transform">
                                <Link
                                    key={product._id}
                                    to={`/product/${product.slug.current}`}
                                    className=""
                                >
                                    <img
                                        src={urlFor(product.mainImage).url()}
                                        alt={product.name}
                                        className="w-[95%] aspect-[7/6] mx-auto object-cover"
                                    />
                                    <div className="p-2 flex flex-col flex-grow">
                                        <h2 className="text-xl font-medium">{product.name}</h2>
                                        <p className="text-base font-OpenSans font-normal tracking-tight leading-normal text-gray-600
                                 line-clamp-2 min-h-[48px]">{product.excerpt}</p>
                                    </div>
                                </Link>
                                <div className="flex justify-between mt-4">
                                    <Link
                                        to={`/product/${product.slug.current}`}
                                        className="mx-2 mb-2 py-2 px-4 bg-[#D4AF37] text-white font-OpenSans font-medium hover:bg-blue-600 transition duration-200"
                                    >
                                        Chi tiết
                                    </Link>
                                    <button
                                        onClick={() => addToWishlist(product)}
                                        className="pb-2 px-4 transition duration-200 text-[#1a1a1a] hover:text-[#000] grayscale"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            width="24"
                                            height="24"
                                            fill="currentColor"
                                            className="w-6 h-6"
                                        >
                                            <path
                                                d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Popup Wishlist */}
            {wishlistPopup && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
                    onClick={closePopup}
                >
                    <div
                        className="bg-white rounded-lg shadow-lg p-6 w-80 sm:w-96"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-semibold text-gray-900 text-center">{wishlistPopup.message}</h3>
                        <div className="flex flex-col items-center mt-4">
                            <img
                                src={urlFor(wishlistPopup.product.mainImage).url()}
                                alt={wishlistPopup.product.name}
                                className="w-32 h-32 object-cover rounded-lg"
                            />
                            <p className="text-gray-800 font-medium mt-2">{wishlistPopup.product.name}</p>
                        </div>
                        <div className="mt-4 flex justify-between">
                            <button
                                onClick={closePopup}
                                className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                            >
                                Đóng
                            </button>
                            <Link
                                to="/wishlist"
                                className="px-4 py-2 bg-[#33367b] text-white rounded hover:bg-blue-600 transition"
                            >
                                Xem danh sách
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
};

export default ProductDetail;
